import { SITE_BASE_URL, BREATHWORK_COUNT, INSTAGRAM_URL } from '../../../constants';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import FadeInOnScroll from '@ui/components/FadeInOnScroll/FadeInOnScroll';
import './landing-animations.css';
import { PageMeta } from '../../seo/PageMeta';
import { JsonLd } from '../../seo/JsonLd';
import HeroSection from './HeroSection';
import ConfigurabilitySection from './ConfigurabilitySection';
import FeaturesSection from './FeaturesSection';
import BreathworkPreviewSection from './BreathworkPreviewSection';
import JourneysSection from './JourneysSection';
import FaqSection from './FaqSection';
import FeelingsSection from './FeelingsSection';
import StoreLinksSection from './StoreLinksSection';
import ClosingCTASection from './ClosingCTASection';
import { ROUTES } from '../../../routes';
import { logEvent } from '../../../infra/firebase/analytics';
import { AnalyticsEvent, AnalyticsParam } from '../../../domain/analytics';
import { useAuth } from '@breath-lab/state';
import { captureReferralParam } from '../../../domain/sharing';
import { createLogger } from '../../../infra/logger';

const log = createLogger('@breath-lab/landing');

export default function LandingPage() {
  const { t } = useTranslation('landing');
  const [searchParams] = useSearchParams();
  const refParam = searchParams.get('ref');
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      if (!user) return; // wait for Firestore user before routing
      navigate(user.onboardedAt ? ROUTES.loggedInHome : ROUTES.welcome, { replace: true });
    }
  }, [isLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    logEvent(AnalyticsEvent.screenView, { [AnalyticsParam.screenName]: 'landing' });
  }, []);

  useEffect(() => {
    if (!refParam) return;
    try {
      captureReferralParam(refParam, user?.uid ?? null);
    } catch (err) {
      log.error('Failed to capture referral param', err);
    }
    logEvent(AnalyticsEvent.referralLinkVisit, {
      [AnalyticsParam.referrerUid]: refParam,
      [AnalyticsParam.contentType]: 'app',
      [AnalyticsParam.id]: '',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refParam]);

  return (
    <>
      <PageMeta
        title={t('meta.title')}
        description={t('meta.description')}
        path="/"
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Breath Lab',
          url: SITE_BASE_URL,
          description:
            `Master ${BREATHWORK_COUNT} traditional breathwork techniques — pranayama rooted in ancient Indian practice — with guided sessions, real-time visual cues, and configurable practice.`,
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Breath Lab',
          url: SITE_BASE_URL,
          logo: SITE_BASE_URL + '/app_icon.png',
          sameAs: [INSTAGRAM_URL],
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Breath Lab',
          applicationCategory: 'HealthApplication',
          operatingSystem: 'Web',
          url: SITE_BASE_URL,
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: (t('faq.items', { returnObjects: true }) as Array<{ question: string; answer: string }>).map(
            (item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: { '@type': 'Answer', text: item.answer },
            }),
          ),
        }}
      />
      <FadeInOnScroll>
        <HeroSection />
      </FadeInOnScroll>
      <FadeInOnScroll delay={0.1}>
        <BreathworkPreviewSection />
      </FadeInOnScroll>
      <FadeInOnScroll delay={0.1}>
        <FeaturesSection />
      </FadeInOnScroll>
      <FadeInOnScroll delay={0.1}>
        <ConfigurabilitySection />
      </FadeInOnScroll>
      <FadeInOnScroll delay={0.1}>
        <FeelingsSection />
      </FadeInOnScroll>
      <JourneysSection />
      <FadeInOnScroll delay={0.1}>
        <FaqSection />
      </FadeInOnScroll>
      <FadeInOnScroll delay={0.1}>
        <StoreLinksSection />
      </FadeInOnScroll>
      <FadeInOnScroll delay={0.1}>
        <ClosingCTASection />
      </FadeInOnScroll>
    </>
  );
}
